/**
 * Duty status state machine.
 *
 * The backend models duty state as a linear machine:
 *
 *     active ↔ inactive ↔ archived
 *
 * Only adjacent transitions are allowed. Non-adjacent jumps
 * (active → archived, archived → active) must step through 'inactive'
 * first; the backend returns 409 if bypassed. This module is the single
 * source of truth the list Actions column and the canvas ToggleGroup
 * share to stay in sync with the 409 contract.
 */

import type { DutyStatus } from '@/features/duties/types'

const NEIGHBORS: Record<DutyStatus, readonly DutyStatus[]> = {
  active: ['inactive'],
  inactive: ['active', 'archived'],
  archived: ['inactive'],
}

/**
 * Narrow an unknown value to ``DutyStatus``. Prefer this over hand-rolled
 * ``v === 'active' || ...`` chains so a future 4th status only requires
 * one edit.
 */
export function isDutyStatus(value: unknown): value is DutyStatus {
  return value === 'active' || value === 'inactive' || value === 'archived'
}

/**
 * Human-readable explanation for why a non-adjacent transition is
 * blocked. Returns null for allowed transitions (including the identity
 * short-circuit where from === to).
 */
export function forbiddenTransitionMessage(
  from: DutyStatus,
  to: DutyStatus
): string | null {
  if (from === to || NEIGHBORS[from].includes(to)) return null
  if (from === 'archived' && to === 'active') {
    return 'Unarchive first — set to inactive, then activate.'
  }
  if (from === 'active' && to === 'archived') {
    return "Can't archive an active duty — deactivate first, then archive."
  }
  // Unreachable with the 3-node linear graph, but defensive.
  return 'Transition not allowed.'
}
